import { useState, useCallback, useEffect, useRef } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { Box, Fade, IconButton, Skeleton, Stack } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const THUMBNAIL_HEIGHT = 80;
const THUMBNAIL_GAP = 10;

const GalleryLoaderSkeleton = ({ height = 400 }) => {
  const mainAreaHeight = Math.max(0, height - THUMBNAIL_HEIGHT - THUMBNAIL_GAP);
  return (
    <Box
      sx={{
        height,
        width: '100%',
        p: 1,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: `${THUMBNAIL_GAP}px`,
      }}
    >
      <Box sx={{ position: 'relative', height: mainAreaHeight }}>
        <Skeleton variant="rectangular" width="100%" height="100%" />
        <Skeleton
          variant="circular"
          width={36}
          height={36}
          sx={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
          }}
        />
        <Skeleton
          variant="circular"
          width={36}
          height={36}
          sx={{
            position: 'absolute',
            right: 12,
            top: '50%',
            transform: 'translateY(-50%)',
          }}
        />
      </Box>
      <Stack direction="row" spacing={1} sx={{ height: THUMBNAIL_HEIGHT }}>
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            sx={{ width: '20%', height: '100%' }}
          />
        ))}
      </Stack>
    </Box>
  );
};

const EmblaGallery = ({
  images,
  thumbnails,
  height = 400,
  backgroundColor = '#FFF6E8',
  objectFit = 'contain',
  onErrorImageURL = 'https://clubdynasty.ca/wp-content/uploads/2021/06/reception-17.jpg',
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const thumbImages = thumbnails || images;
  const imageCount = images?.length ?? 0;
  const showControls = imageCount > 1;

  // Main carousel
  const [mainRef, mainApi] = useEmblaCarousel({ loop: true });
  // Thumbnail carousel
  const [thumbRef, thumbApi] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true,
  });

  // Sync main → thumb
  const onMainSelect = useCallback(() => {
    if (!mainApi || !thumbApi) return;
    const index = mainApi.selectedScrollSnap();
    setSelectedIndex(index);
    thumbApi.scrollTo(index);
  }, [mainApi, thumbApi]);

  useEffect(() => {
    if (!mainApi) return;
    mainApi.on('select', onMainSelect);
    onMainSelect();
    return () => mainApi.off('select', onMainSelect);
  }, [mainApi, onMainSelect]);

  // Click thumb → scroll main
  const onThumbClick = useCallback(
    (index) => {
      if (mainApi) mainApi.scrollTo(index);
    },
    [mainApi],
  );

  const scrollPrev = useCallback(() => mainApi?.scrollPrev(), [mainApi]);
  const scrollNext = useCallback(() => mainApi?.scrollNext(), [mainApi]);

  // Fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    if (showControls) setIsFullscreen((prev) => !prev);
  }, [showControls]);

  // Keyboard navigation
  const containerRef = useRef(null);
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        scrollPrev();
      } else if (e.key === 'ArrowRight') {
        scrollNext();
      } else if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [scrollPrev, scrollNext, isFullscreen]);

  // Handle image error
  const handleImageError = useCallback(
    (e) => {
      if (onErrorImageURL) e.target.src = onErrorImageURL;
    },
    [onErrorImageURL],
  );

  if (!images || images.length === 0) return null;

  const containerSx = isFullscreen
    ? {
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        backgroundColor: 'rgba(0,0,0,0.95)',
        padding:
          'env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)',
        display: 'flex',
        flexDirection: 'column',
      }
    : {
        width: '100%',
        backgroundColor,
        position: 'relative',
        minHeight: height,
        overflow: 'hidden',
      };

  const slideHeight = isFullscreen ? 'calc(100dvh - 110px)' : height;

  return (
    <Box ref={containerRef} sx={containerSx}>
      {/* Skeleton fades out */}
      {!isFullscreen && (
        <Fade in={!imageLoaded} timeout={400}>
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 1,
              pointerEvents: 'none',
            }}
          >
            <GalleryLoaderSkeleton height={height} />
          </Box>
        </Fade>
      )}

      {/* Main viewport */}
      <Box
        sx={{
          flex: isFullscreen ? 1 : 'none',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div ref={mainRef} style={{ overflow: 'hidden', height: slideHeight }}>
          <div
            style={{
              display: 'flex',
              touchAction: 'pan-y pinch-zoom',
              height: '100%',
            }}
          >
            {images.map((src, index) => (
              <div
                key={index}
                style={{
                  flex: '0 0 100%',
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: showControls ? 'pointer' : 'default',
                }}
                onClick={toggleFullscreen}
              >
                <img
                  src={src}
                  alt={`Gallery ${index + 1}`}
                  loading="lazy"
                  onLoad={index === 0 ? () => setImageLoaded(true) : undefined}
                  onError={handleImageError}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit,
                    display: 'block',
                  }}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Nav arrows */}
        {showControls && (
          <>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                scrollPrev();
              }}
              sx={{
                position: 'absolute',
                left: 'calc(8px + env(safe-area-inset-left, 0px))',
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(0,0,0,0.5)',
                color: '#fff',
                width: 44,
                height: 44,
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
              }}
              aria-label="Previous image"
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                scrollNext();
              }}
              sx={{
                position: 'absolute',
                right: 'calc(8px + env(safe-area-inset-right, 0px))',
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(0,0,0,0.5)',
                color: '#fff',
                width: 44,
                height: 44,
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
              }}
              aria-label="Next image"
            >
              <ChevronRightIcon />
            </IconButton>
          </>
        )}

        {/* Fullscreen button */}
        {showControls && !isFullscreen && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              toggleFullscreen();
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              backgroundColor: 'rgba(0,0,0,0.5)',
              color: '#fff',
              width: 36,
              height: 36,
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
            }}
            aria-label="Enter fullscreen"
          >
            <FullscreenIcon fontSize="small" />
          </IconButton>
        )}

        {/* Close button in fullscreen */}
        {isFullscreen && (
          <IconButton
            onClick={() => setIsFullscreen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: '#fff',
              width: 44,
              height: 44,
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' },
            }}
            aria-label="Exit fullscreen"
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* Thumbnails */}
      {showControls && (
        <Box
          sx={{
            py: 1,
            px: 0.5,
            backgroundColor: isFullscreen ? 'rgba(0,0,0,0.8)' : 'transparent',
          }}
        >
          <div ref={thumbRef} style={{ overflow: 'hidden' }}>
            <div style={{ display: 'flex', gap: 8 }}>
              {thumbImages.map((src, index) => (
                <button
                  key={index}
                  onClick={() => onThumbClick(index)}
                  type="button"
                  style={{
                    flex: `0 0 ${THUMBNAIL_HEIGHT}px`,
                    height: THUMBNAIL_HEIGHT,
                    border:
                      index === selectedIndex
                        ? '2px solid #c3892b'
                        : '2px solid transparent',
                    borderRadius: 4,
                    padding: 0,
                    cursor: 'pointer',
                    overflow: 'hidden',
                    opacity: index === selectedIndex ? 1 : 0.6,
                    transition: 'opacity 0.2s, border-color 0.2s',
                    background: 'none',
                  }}
                >
                  <img
                    src={src}
                    alt={`Thumbnail ${index + 1}`}
                    loading="lazy"
                    onError={handleImageError}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                </button>
              ))}
            </div>
          </div>
        </Box>
      )}
    </Box>
  );
};

export default EmblaGallery;
